import { Alert, Box, Typography } from '@mui/material';
import { useQuery } from 'react-query';
import { Loader } from '../../../components';
import { useMemo, useState } from 'react';
import { format, subHours, startOfHour } from 'date-fns';
import { theme } from '../../../styles/theme';
import { downtimeDectionTypesQueryKey, getDowntimeDectionTypes, downtimeDectionsQueryKey, getDowntimeDections } from '../../../fetch';
import { IDropdownResponse, IDowntimeDetection } from '../../../models';
import { ResponsiveLine } from '@nivo/line';

// const testData = [
//   {
//     downtimeDetectionId: 12,
//     downtimeDetectionId: 12,
//     type: 'SageDb',
//     status: 'Unhealthy',
//     detectedAt: '2025-04-14T13:54:53.7070095+00:00'
//     detectedAt: '2025-04-14T13:54:53.7070095+00:00'
//   },
//   {
//     downtimeDetectionId: 11,
//     downtimeDetectionId: 11,
//     type: 'SageDb',
//     status: 'Unhealthy',
//     detectedAt: '2025-04-14T13:53:00.0445305+00:00'
//     detectedAt: '2025-04-14T13:53:00.0445305+00:00'
//   },
//   {
//     downtimeDetectionId: 10,
//     downtimeDetectionId: 10,
//     type: 'SageDb',
//     status: 'Unhealthy',
//     detectedAt: '2025-04-14T13:52:42.7938872+00:00'
//     detectedAt: '2025-04-14T13:52:42.7938872+00:00'
//   },
//   {
//     downtimeDetectionId: 9,
//     downtimeDetectionId: 9,
//     type: 'SageDb',
//     status: 'Unhealthy',
//     detectedAt: '2025-04-14T13:51:00.0069047+00:00'
//     detectedAt: '2025-04-14T13:51:00.0069047+00:00'
//   },
//   {
//     downtimeDetectionId: 8,
//     type: 'SageDb',
//     downtimeDetectionId: 8,
//     type: 'SageDb',
//     status: 'Unhealthy',
//     detectedAt: '2025-04-14T13:50:32.654756+00:00'
//     detectedAt: '2025-04-14T13:50:32.654756+00:00'
//   },
//   {
//     downtimeDetectionId: 7,
//     type: 'SageDb',
//     type: 'SageDb',
//     status: 'Unhealthy',
//     detectedAt: '2025-04-14T14:50:31.8475084+00:00'
//   }
// ];

const buildLineChartData = (data: IDowntimeDetection[], type: string) => {
  // Create an array of 3-hour increments for the past 24 hours
  const hours = Array.from({ length: 8 }, (_, i) =>
    format(subHours(startOfHour(new Date()), (7 - i) * 3), 'yyyy-MM-dd HH:00:00')
  );
  // Initialize the series for the selected type
  const series = {
    id: type,
    color: theme.palette.error.main,
    data: hours.map(hour => ({
      // three hour increments
      x: hour,
      y: 0 // Default to 0 for each hour
    }))
  };
  // Populate the series with data points
  data
    .filter(item => item.type === type && item.status === 'Unhealthy')
    .forEach(item => {
      const detectedHour = format(new Date(item.detectedAt), 'yyyy-MM-dd HH:00:00');
      const dataPoint = series.data.find(dp => dp.x === detectedHour);
      if (dataPoint) {
        dataPoint.y += 1; // Increment the count for the hour
      } else {
        // If the hour is not found, you can choose to add it or ignore
        series.data.push({ x: detectedHour, y: 1 });
      }
    });
  // Sort the data points to ensure they are in chronological order
  series.data.sort((a, b) => new Date(a.x).getTime() - new Date(b.x).getTime());

  return [series];
};

export const SageConnectivityStatusDetails = () => {
  const [selectedType, setSelectedType] = useState<string>('');

  useQuery<IDropdownResponse[], Error>(
    [downtimeDectionTypesQueryKey],
    () => getDowntimeDectionTypes(),
    {
      onSuccess: d => {
        setSelectedType(d[0]?.value);
      }
    }
  );
  const { isLoading, data } = useQuery<IDowntimeDetection[], Error>(
    [downtimeDectionsQueryKey, selectedType],
    () => getDowntimeDections(),
    {
      enabled: !!selectedType
    }
  );
  const chartData = useMemo(() => {
    if (!data || !selectedType) return [];
    return buildLineChartData(data, selectedType);
  }, [data, selectedType]);

  const maxYValue = useMemo(() => {
    return chartData
      ? Math.max(...chartData?.map(serie => Math.max(...serie?.data?.map(dp => dp.y as number))))
      : 30;
  }, [chartData]);

  const hasCurrentErrors = useMemo(() => {
    const currentHour = format(new Date(), 'yyyy-MM-dd HH:00:00');
    // Find the data point for the current hour
    // and check if the y value is greater than 0
    const currentHourData = chartData
      ?.find(serie => serie.id === selectedType)
      ?.data.find(dp => dp.x === currentHour);
    if (currentHourData) {
      return currentHourData.y > 0;
    }
  }, [chartData, selectedType]);

  return (
    <Box>
      <Alert severity={hasCurrentErrors ? 'error' : 'success'} sx={{ mb: 2 }}>
        Sage Database is currently {hasCurrentErrors ? 'unhealthy' : 'healthy'}.
      </Alert>
      <Typography variant="h6" color="secondary">
        This chart shows Sage database connection downtime to BuilderLinq for the past 24 Hours.
      </Typography>
      {isLoading && <Loader type="inline" position="centered" />}
      {chartData && (
        <Box sx={{ height: 350 }}>
          <ResponsiveLine
            data={chartData ?? []}
            margin={{ top: 20, right: 40, bottom: 50, left: 40 }}
            yScale={{
              max: maxYValue + 10,
              type: 'linear'
            }}
            axisTop={null}
            enableArea
            axisRight={null}
            animate={false}
            useMesh
            enableGridX={false}
            enableGridY={false}
            pointColor={theme.palette.error.main}
            pointSize={10}
            axisBottom={{
              format: (value: string) => {
                const readableTime = new Intl.DateTimeFormat('en-US', {
                  hour: 'numeric',
                  minute: 'numeric',
                  hour12: true
                }).format(new Date(`1970-01-01T${value.split(' ')?.[1]}`));
                return readableTime;
              }
            }}
            axisLeft={{
              tickSize: 10,
              tickPadding: 0,
              tickRotation: 0,
              legend: 'Occurrences',
              legendPosition: 'middle',
              legendOffset: -30
            }}
          />
        </Box>
      )}
    </Box>
  );
};
